import { css } from "lit";

export const styles = css`
  .user-background-image {
    position: relative;
    box-sizing: border-box;
    width: 100%;
    min-height: 200px;
    aspect-ratio: 27 / 14;
    border-radius: var(--sds-size-radius-400);
    background-color: var(--sds-color-background-default-tertiary);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;
    transition: all 0.2s ease;
    overflow: hidden;
  }

  .user-background-image:hover {
    box-shadow: 0 4px 12px rgb(0 0 0 / 0.15);
  }

  .user-background-image.has-background {
    background-color: transparent;
  }

  .user-background-image:focus {
    outline: var(--sds-size-stroke-focus-ring) solid var(--sds-color-border-brand-default);
    outline-offset: var(--sds-size-stroke-focus-ring);
  }

  .camera-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: start;
    padding-top: var(--sds-size-space-200);
    background: var(--sds-color-background-utilities-overlay);
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
    z-index: 1;
  }

  .camera-overlay.onboarding {
    opacity: 0.8;
  }

  @media (min-width: 1024px) {
    .camera-overlay {
      justify-content: center;
      padding-top: initial;
    }
  }

  .user-background-image:not(.has-background) .camera-overlay {
    opacity: 1;
    background: transparent;
  }

  .user-background-image:hover .camera-overlay,
  .camera-button.dragging .camera-overlay {
    opacity: 1;
  }

  .camera-circle {
    width: 40px;
    height: 40px;
    border-radius: var(--sds-size-radius-full);
    background-color: var(--sds-color-white-100);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--sds-size-space-200);
    transition: all 0.2s ease;
  }

  .user-background-image:hover .camera-circle {
    background-color: var(--sds-color-white-200);
    transform: scale(1.1);
  }

  .camera-icon {
    color: var(--sds-color-white-400);
    opacity: 0.5;
  }

  .camera-button {
    height: 100%;
  }

  .camera-text {
    color: var(--sds-color-text-brand-on-brand);
    font-size: var(--sds-typography-body-size-small);
    font-weight: var(--sds-typography-weight-medium);
    font-family: var(--sds-typography-family-body);
    text-shadow: 0 1px 2px var(--sds-color-black-400);
    text-align: center;
  }

  .user-background-image:not(.has-background) .camera-text {
    color: var(--sds-color-text-default-secondary);
    text-shadow: none;
  }

  .user-profile-overlay {
    position: absolute;
    width: fit-content;
    left: var(--sds-size-space-800);
    right: var(--sds-size-space-800);
    bottom: var(--sds-size-space-800);
    display: grid;
    grid-template:
      "avatar name" auto
      "social social" auto
      / 64px 1fr;
    gap: var(--sds-size-space-600) var(--sds-size-space-600);
    row-gap: var(--sds-size-space-300);
    z-index: 1;
  }

  .user-name {
    grid-area: name;
    font-family: var(--sds-typography-family-title);
    font-size: var(--sds-typography-heading-size);
    font-weight: var(--sds-typography-weight-medium);
    line-height: 1.5em;
    color: var(--sds-color-text-default-default);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    align-self: center;
    text-shadow: 0 0 2px var(--sds-color-background-utilities-overlay);
  }

  .social-links {
    --wow-button-inner-width: 32px;
    grid-area: social;
    display: flex;
    gap: var(--sds-size-space-300);
    align-items: center;
  }

  .social-link-button {
    width: 32px;
    height: 32px;
    border-radius: var(--sds-size-radius-full);
    background-color: var(--theme-secondary-color, var(--sds-color-background-neutral-secondary));
    border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: default;

    transition: background-color 0.2s ease;
  }

  .social-link-icon {
    color: var(--theme-primary-color, var(--sds-color-icon-neutral-on-neutral-secondary));
    font-size: var(--sds-typography-body-size-small);
    font-weight: var(--sds-typography-weight-medium);
    width: 16px;
    height: 16px;

    transition: color 0.2s ease;
  }

  .social-link-button .social-link-icon {
    color: var(--theme-primary-color, var(--sds-color-white-500));
  }

  .uploading .camera-overlay {
    opacity: 1;
  }

  .uploading .camera-circle {
    background-color: var(--sds-color-background-brand-default);
  }

  .uploading .camera-icon {
    color: var(--sds-color-icon-brand-on-brand);
    opacity: 1;
  }

  .background-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--sds-size-radius-400);
    z-index: 1;
    pointer-events: none;
  }

  .user-background-image.is-video {
    background-color: transparent;
  }

  .user-background-image.is-video::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgb(0 0 0 / 0.1) 0%, rgb(0 0 0 / 0.2) 50%, rgb(0 0 0 / 0.6) 100%);
    border-radius: var(--sds-size-radius-400);
    z-index: 2;
    pointer-events: none;
  }

  .user-background-image.is-video .camera-overlay,
  .user-background-image.is-video .user-profile-overlay {
    z-index: 3;
  }

  .user-background-image.is-video .camera-button {
    z-index: 3;
  }

  .gradient-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgb(17 17 17 / 0) 0%, var(--sds-color-background-utilities-overlay) 100%);
    z-index: 1;
    pointer-events: none;
  }
`;
